/**
 * First-Pay-On-Time Rate Card.
 *
 * Leading indicator the all-time collection rate hides: of invoices issued
 * in the last 60 days that are now past due (judgeable), what percentage
 * were paid in full by their due_date? A drop here predicts a drop in
 * cash flow weeks before it shows up in collections totals.
 */

import React from "react";
import { CardContent } from "@mui/material";
import { Schedule as OnTimeIcon } from "@mui/icons-material";
import { StatCardFrame, StatCardHead } from "./StatCard";

interface FirstPayOnTimeCardProps {
  rate?: number;
  judgeable?: number;
  onClick?: () => void;
}

const colorFor = (rate: number): "success" | "warning" | "error" => {
  if (rate >= 85) return "success";
  if (rate >= 70) return "warning";
  return "error";
};

export const FirstPayOnTimeCard: React.FC<FirstPayOnTimeCardProps> = ({
  rate,
  judgeable,
  onClick,
}) => {
  const sample = judgeable ?? 0;
  const hasData = sample > 0 && rate !== undefined;
  const color = hasData ? colorFor(rate) : "info";
  const value = hasData ? `${rate.toFixed(1)}%` : "—";
  const subtitle = hasData
    ? `${sample} invoice${sample === 1 ? "" : "s"} · last 60 days`
    : "Not enough data yet";

  return (
    <StatCardFrame
      onClick={onClick}
      ariaLabel={onClick ? `On-Time Rate, ${value}, ${subtitle}` : undefined}
    >
      <CardContent sx={{ p: 2.25, flex: 1, "&:last-child": { pb: 2.25 } }}>
        <StatCardHead
          label="On-Time Rate"
          tooltip="Of invoices issued in the last 60 days that are now past due, the percentage paid in full by their due date. Leading indicator of recent payer behaviour."
          icon={<OnTimeIcon />}
          color={color}
          value={value}
          valueColor={hasData ? `${color}.main` : "text.primary"}
          subtitle={subtitle}
        />
      </CardContent>
    </StatCardFrame>
  );
};
